import React, { useEffect, useMemo, useState } from 'react';
import { userService } from '../../services/userService';
import { aboutVideoService, AboutVideo } from '../../services/aboutVideoService';
import { User } from '../../types';
import { useToast } from '../../components/Toast';
import { getMediaUrl } from '../../utils/media';

type NewUserForm = {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: User['role'];
  phone: string;
  address: string;
};

const initialNewUser: NewUserForm = {
  username: '',
  email: '',
  full_name: '',
  password: '',
  role: 'student',
  phone: '',
  address: '',
};

const AdminUsersPage: React.FC = () => {
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [userQuery, setUserQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState<NewUserForm>(initialNewUser);
  const [newUserAvatarFile, setNewUserAvatarFile] = useState<File | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<{
    username: string;
    email: string;
    full_name: string;
    role: User['role'];
    password: string;
    phone: string;
    address: string;
  }>({
    username: '',
    email: '',
    full_name: '',
    role: 'student',
    password: '',
    phone: '',
    address: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingAvatarFile, setEditingAvatarFile] = useState<File | null>(null);
  const [lockDialogUser, setLockDialogUser] = useState<User | null>(null);
  const [lockReason, setLockReason] = useState('');
  const [lockingUser, setLockingUser] = useState(false);
  const [fakeUsersEnabled, setFakeUsersEnabled] = useState(false);
  const [totalFakeUsers, setTotalFakeUsers] = useState(0);
  const [loadingFakeUsersSetting, setLoadingFakeUsersSetting] = useState(false);

  // About videos (for About page)
  const [aboutVideos, setAboutVideos] = useState<AboutVideo[]>([]);
  const [aboutVideosLoading, setAboutVideosLoading] = useState(false);
  const [aboutVideoForm, setAboutVideoForm] = useState<Partial<AboutVideo>>({
    title: '',
    youtube_link: '',
    is_active: true,
    display_order: 0,
  });
  const [editingAboutVideoId, setEditingAboutVideoId] = useState<number | null>(null);
  const [savingAboutVideo, setSavingAboutVideo] = useState(false);
  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }),
    []
  );
  const totalBalance = useMemo(
    () => users.reduce((sum, user) => sum + Number(user.balance || 0), 0),
    [users]
  );
  const userCount = users.length;
  const averageBalance = useMemo(() => (userCount ? totalBalance / userCount : 0), [totalBalance, userCount]);
  const highestBalanceUser = useMemo(() => {
    if (users.length === 0) return null;
    return users.reduce((prev, current) => {
      const prevBalance = Number(prev.balance || 0);
      const currentBalance = Number(current.balance || 0);
      return currentBalance > prevBalance ? current : prev;
    });
  }, [users]);
  const formatBalance = (value?: number) => currencyFormatter.format(value ?? 0);

  const formatTaothaoUsage = (usage?: string) => {
    if (!usage) return 'Chưa có';
    switch (usage) {
      case 'veo3.1': return 'Veo3.1 Auto Video Creator';
      case 'whisk': return 'Whisk Image Creator';
      case 'combo': return 'Combo All In One';
      case 'never': return 'Chưa dùng bao giờ';
      default: return usage;
    }
  };

  const handleChangePage = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    fetchUsers({ page: newPage });
  };

  const currentTotal = totalUsers || users.length;
  const startIndex = currentTotal === 0 ? 0 : (page - 1) * perPage + 1;
  const endIndex = currentTotal === 0 ? 0 : Math.min(currentTotal, page * perPage);

  const fetchUsers = async (options?: { page?: number; perPage?: number }) => {
    const targetPage = options?.page ?? page;
    const targetPerPage = options?.perPage ?? perPage;

    setUsersLoading(true);
    try {
      const data = await userService.listUsers({
        q: userQuery || undefined,
        role: userRoleFilter || undefined,
        page: targetPage,
        per_page: targetPerPage,
      });
      setUsers(data.users);
      setTotalUsers(data.total);
      setTotalPages(data.pages || 1);
      setPage(data.page || targetPage);
      setPerPage(data.per_page || targetPerPage);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchFakeUsersSetting();
    fetchAboutVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        username: newUser.username.trim(),
        email: newUser.email.trim(),
        password: newUser.password,
        full_name: newUser.full_name.trim(),
        role: newUser.role,
      };
      const { user } = await userService.createUser(payload);
      if (newUserAvatarFile) {
        const updated = await userService.uploadAvatar(user.id, newUserAvatarFile);
        setUsers((prev) => [updated, ...prev]);
      } else {
        setUsers((prev) => [user, ...prev]);
      }
      setNewUser(initialNewUser);
      setNewUserAvatarFile(null);
      setShowCreateForm(false);
      alert('Tạo người dùng thành công');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể tạo người dùng');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u: User) => {
    setEditingUserId(u.id);
    setEditingData({
      username: u.username,
      email: u.email,
      full_name: u.full_name || '',
      role: u.role,
      password: '',
      phone: u.phone || '',
      address: u.address || '',
    });
  };

  const cancelEdit = () => {
    setEditingUserId(null);
    setEditingData({
      username: '',
      email: '',
      full_name: '',
      role: 'student',
      password: '',
      phone: '',
      address: '',
    });
    setEditingAvatarFile(null);
  };

  const saveEdit = async (userId: number) => {
    setSavingEdit(true);
    try {
      const payload: any = {
        username: editingData.username.trim(),
        email: editingData.email.trim(),
        full_name: editingData.full_name.trim(),
        role: editingData.role,
      };
      if (editingData.phone) payload.phone = editingData.phone.trim();
      if (editingData.address) payload.address = editingData.address.trim();
      if (editingData.password) {
        payload.password = editingData.password;
      }
      const { user } = await userService.updateUser(userId, payload);
      let finalUser = user;
      if (editingAvatarFile) {
        finalUser = await userService.uploadAvatar(userId, editingAvatarFile);
      }
      setUsers((list) => list.map((it) => (it.id === userId ? finalUser : it)));
      cancelEdit();
      alert('Cập nhật người dùng thành công');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể cập nhật người dùng');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteUser = async (u: User) => {
    if (!window.confirm(`Xóa người dùng "${u.username}"?`)) return;
    try {
      await userService.deleteUser(u.id);
      setUsers((list) => list.filter((it) => it.id !== u.id));
      if (editingUserId === u.id) {
        cancelEdit();
      }
    } catch {
      alert('Không thể xóa người dùng');
    }
  };

  const handleUnlockUser = async (u: User) => {
    if (!window.confirm(`Mở khóa tài khoản "${u.username}"?`)) return;
    try {
      const { user } = await userService.unlockUser(u.id);
      setUsers((list) => list.map((it) => (it.id === u.id ? user : it)));
      showToast('Đã mở khóa tài khoản thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể mở khóa tài khoản', 'error');
    }
  };

  const handleLockUser = async (u: User) => {
    setLockDialogUser(u);
    setLockReason('');
  };

  const confirmLockUser = async () => {
    if (!lockDialogUser) return;
    setLockingUser(true);
    try {
      const { user } = await userService.lockUser(lockDialogUser.id, {
        reason: lockReason.trim() || undefined,
      });
      setUsers((list) => list.map((it) => (it.id === lockDialogUser.id ? user : it)));
      showToast('Đã khóa tài khoản thành công', 'success');
      setLockDialogUser(null);
      setLockReason('');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể khóa tài khoản', 'error');
    } finally {
      setLockingUser(false);
    }
  };

  const cancelLockDialog = () => {
    setLockDialogUser(null);
    setLockReason('');
    setLockingUser(false);
  };

  const handleShowLockedUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await userService.getLockedUsers();
      setUsers(data.users);
      setTotalUsers(data.total || data.users.length || 0);
      setTotalPages(1);
      setPage(1);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể tải danh sách tài khoản bị khóa');
    } finally {
      setUsersLoading(false);
    }
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    setNewUser(initialNewUser);
    setNewUserAvatarFile(null);
  };

  const handleToggleKeyAccess = async (u: User) => {
    const newValue = !u.can_view_keys;
    try {
      const { user } = await userService.toggleKeyAccess(u.id, newValue);
      setUsers((list) => list.map((it) => (it.id === u.id ? user : it)));
      showToast(
        newValue
          ? `Đã kích hoạt quyền xem key cho ${u.username}`
          : `Đã tắt quyền xem key cho ${u.username}`,
        'success'
      );
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể cập nhật quyền xem key', 'error');
    }
  };

  const fetchFakeUsersSetting = async () => {
    setLoadingFakeUsersSetting(true);
    try {
      const data = await userService.getFakeUsersSetting();
      setFakeUsersEnabled(data.fake_users_enabled);
      setTotalFakeUsers(data.total_fake_users);
    } catch (err: any) {
      console.error('Không thể tải cài đặt fake users:', err);
    } finally {
      setLoadingFakeUsersSetting(false);
    }
  };

  const handleToggleFakeUsers = async () => {
    const newValue = !fakeUsersEnabled;
    setLoadingFakeUsersSetting(true);
    try {
      await userService.toggleFakeUsers(newValue);
      setFakeUsersEnabled(newValue);
      showToast(
        newValue
          ? 'Đã bật chức năng tự động thêm fake users'
          : 'Đã tắt chức năng tự động thêm fake users',
        'success'
      );
      // Refresh để cập nhật số lượng fake users
      await fetchFakeUsersSetting();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể cập nhật cài đặt fake users', 'error');
    } finally {
      setLoadingFakeUsersSetting(false);
    }
  };

  const fetchAboutVideos = async () => {
    setAboutVideosLoading(true);
    try {
      const data = await aboutVideoService.list({ per_page: 100 });
      setAboutVideos(data.videos);
    } catch (err: any) {
      console.error('Không thể tải danh sách Video About:', err);
      showToast('Không thể tải danh sách Video About', 'error');
    } finally {
      setAboutVideosLoading(false);
    }
  };

  const resetAboutVideoForm = () => {
    setAboutVideoForm({
      title: '',
      youtube_link: '',
      is_active: true,
      display_order: 0,
    });
    setEditingAboutVideoId(null);
  };

  const handleSaveAboutVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aboutVideoForm.title || !aboutVideoForm.youtube_link) {
      showToast('Vui lòng nhập Tiêu đề và Link YouTube', 'warning');
      return;
    }

    setSavingAboutVideo(true);
    try {
      if (editingAboutVideoId) {
        await aboutVideoService.update(editingAboutVideoId, aboutVideoForm);
        showToast('Cập nhật Video About thành công', 'success');
      } else {
        await aboutVideoService.create(aboutVideoForm);
        showToast('Thêm Video About thành công', 'success');
      }
      resetAboutVideoForm();
      fetchAboutVideos();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể lưu Video About', 'error');
    } finally {
      setSavingAboutVideo(false);
    }
  };

  const handleEditAboutVideo = (video: AboutVideo) => {
    setEditingAboutVideoId(video.id!);
    setAboutVideoForm({
      title: video.title,
      youtube_link: video.youtube_link,
      is_active: video.is_active,
      display_order: video.display_order,
    });
  };

  const handleDeleteAboutVideo = async (video: AboutVideo) => {
    if (!video.id) return;
    if (!window.confirm(`Xóa Video About "${video.title}"?`)) return;

    try {
      await aboutVideoService.delete(video.id);
      showToast('Đã xóa Video About', 'success');
      fetchAboutVideos();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể xóa Video About', 'error');
    }
  };

  return (
    <>
      {/* About Videos Section (for About page) */}
      <div className="mb-6 bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Video About (Trang giới thiệu)</h2>
            <p className="text-sm text-gray-500">
              Admin thêm danh sách video sẽ hiển thị ở trang <span className="font-medium">/about</span>.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSaveAboutVideo}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4"
        >
          <label className="space-y-1">
            <span className="text-xs font-medium text-gray-700 block">
              Tiêu đề <span className="text-red-500">*</span>
            </span>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={aboutVideoForm.title || ''}
              onChange={(e) =>
                setAboutVideoForm((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Tiêu đề video"
              required
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs font-medium text-gray-700 block">
              Link YouTube <span className="text-red-500">*</span>
            </span>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={aboutVideoForm.youtube_link || ''}
              onChange={(e) =>
                setAboutVideoForm((prev) => ({ ...prev, youtube_link: e.target.value }))
              }
              placeholder="https://www.youtube.com/watch?v=..."
              required
            />
          </label>

          <label className="space-y-1">
            <span className="text-xs font-medium text-gray-700 block">Thứ tự hiển thị</span>
            <input
              type="number"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={aboutVideoForm.display_order ?? 0}
              onChange={(e) =>
                setAboutVideoForm((prev) => ({
                  ...prev,
                  display_order: Number(e.target.value) || 0,
                }))
              }
            />
          </label>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={aboutVideoForm.is_active ?? true}
                onChange={(e) =>
                  setAboutVideoForm((prev) => ({
                    ...prev,
                    is_active: e.target.checked,
                  }))
                }
              />
              <span className="text-xs text-gray-700">Đang hiển thị</span>
            </label>

            <button
              type="submit"
              disabled={savingAboutVideo}
              className="ml-auto px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {savingAboutVideo
                ? 'Đang lưu...'
                : editingAboutVideoId
                ? 'Cập nhật Video'
                : 'Thêm Video'}
            </button>

            {editingAboutVideoId && (
              <button
                type="button"
                onClick={resetAboutVideoForm}
                className="px-3 py-2 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Hủy sửa
              </button>
            )}
          </div>
        </form>

        <div className="border-t border-gray-200 pt-3">
          {aboutVideosLoading ? (
            <p className="text-sm text-gray-500">Đang tải danh sách Video About...</p>
          ) : aboutVideos.length === 0 ? (
            <p className="text-sm text-gray-500">Chưa có Video About nào.</p>
          ) : (
            <div className="space-y-2">
              {aboutVideos.map((video) => (
                <div
                  key={video.id}
                  className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 text-sm"
                >
                  <div className="min-w-0 mr-3">
                    <p className="font-medium text-gray-900 truncate">{video.title}</p>
                    <p className="text-xs text-gray-500 truncate">{video.youtube_link}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        video.is_active
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-gray-50 text-gray-600 border border-gray-200'
                      }`}
                    >
                      {video.is_active ? 'Đang hiển thị' : 'Đã tắt'}
                    </span>
                    <span className="text-xs text-gray-400">
                      #{video.display_order ?? 0}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleEditAboutVideo(video)}
                      className="px-2 py-1 rounded border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Sửa
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteAboutVideo(video)}
                      className="px-2 py-1 rounded border border-red-500 text-xs text-red-600 hover:bg-red-50"
                    >
                      Xóa
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Lock User Modal */}
      {lockDialogUser && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={cancelLockDialog}>
          <div
            className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Khóa tài khoản</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Người dùng: <span className="font-medium text-gray-900">{lockDialogUser.username}</span>
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {`Nhập lý do khóa tài khoản "${lockDialogUser.username}" (tuỳ chọn):`}
                <textarea
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  rows={3}
                  placeholder="Ví dụ: vi phạm nội dung, spam, v.v..."
                  value={lockReason}
                  onChange={(e) => setLockReason(e.target.value)}
                />
              </label>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-sm text-yellow-800">
                ⚠️ Tài khoản sẽ không thể đăng nhập cho đến khi được mở khóa.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={cancelLockDialog}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                disabled={lockingUser}
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={confirmLockUser}
                disabled={lockingUser}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {lockingUser ? 'Đang khóa...' : 'Khóa tài khoản'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUserId !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={cancelEdit}
        >
          <div
            className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Chỉnh sửa người dùng</h2>
                <p className="text-sm text-gray-500 mt-0.5">Cập nhật thông tin người dùng</p>
              </div>
              <button
                type="button"
                onClick={cancelEdit}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light w-8 h-8 flex items-center justify-center rounded transition-colors"
                title="Đóng"
              >
                ×
              </button>
            </div>

            {/* Form */}
            <form className="p-6 space-y-4" onSubmit={(e) => { e.preventDefault(); saveEdit(editingUserId); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">
                    Username <span className="text-red-500">*</span>
                  </span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.username}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, username: e.target.value }))}
                    placeholder="Nhập username"
                    required
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">
                    Email <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.email}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="example@email.com"
                    required
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Họ tên</span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.full_name}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Họ và tên đầy đủ"
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Vai trò</span>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.role}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, role: e.target.value as User['role'] }))}
                  >
                    <option value="student">Học viên</option>
                    <option value="instructor">Giảng viên</option>
                    <option value="admin">Quản trị</option>
                  </select>
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Số điện thoại</span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.phone}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="0123456789"
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Địa chỉ</span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.address}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, address: e.target.value }))}
                    placeholder="Địa chỉ đầy đủ"
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Mật khẩu mới</span>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={editingData.password}
                    onChange={(e) => setEditingData((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="Để trống nếu giữ nguyên mật khẩu"
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Avatar</span>
                  <div className="border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:border-gray-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full text-sm"
                      onChange={(e) => setEditingAvatarFile(e.target.files?.[0] || null)}
                    />
                    {editingAvatarFile && (
                      <p className="text-xs text-gray-600 mt-1">Đã chọn: {editingAvatarFile.name}</p>
                    )}
                  </div>
                </label>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700 transition-colors font-medium text-sm"
                  disabled={savingEdit}
                >
                  {savingEdit ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Đang lưu...
                    </span>
                  ) : (
                    'Lưu thay đổi'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={closeCreateForm}
        >
          <div
            className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Thêm người dùng mới</h2>
                <p className="text-sm text-gray-500 mt-0.5">Điền thông tin để tạo tài khoản mới</p>
              </div>
              <button
                type="button"
                onClick={closeCreateForm}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light w-8 h-8 flex items-center justify-center rounded transition-colors"
                title="Đóng"
              >
                ×
              </button>
            </div>

            {/* Form */}
            <form className="p-6 space-y-4" onSubmit={handleCreateUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">
                    Username <span className="text-red-500">*</span>
                  </span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.username}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, username: e.target.value }))}
                    placeholder="Nhập username"
                    required
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">
                    Email <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.email}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="example@email.com"
                    required
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Họ tên</span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Họ và tên đầy đủ"
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">
                    Mật khẩu <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.password}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="Mật khẩu tối thiểu 6 ký tự"
                    required
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Avatar</span>
                  <div className="border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:border-gray-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full text-sm"
                      onChange={(e) => setNewUserAvatarFile(e.target.files?.[0] || null)}
                    />
                    {newUserAvatarFile && (
                      <p className="text-xs text-gray-600 mt-1">Đã chọn: {newUserAvatarFile.name}</p>
                    )}
                  </div>
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Số điện thoại</span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.phone}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="0123456789"
                  />
                </label>

                <label className="space-y-1.5 md:col-span-2">
                  <span className="text-sm font-medium text-gray-700 block">Địa chỉ</span>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.address}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, address: e.target.value }))}
                    placeholder="Địa chỉ đầy đủ"
                  />
                </label>

                <label className="space-y-1.5">
                  <span className="text-sm font-medium text-gray-700 block">Vai trò</span>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors outline-none"
                    value={newUser.role}
                    onChange={(e) => setNewUser((prev) => ({ ...prev, role: e.target.value as User['role'] }))}
                  >
                    <option value="student">Học viên</option>
                    <option value="instructor">Giảng viên</option>
                    <option value="admin">Quản trị</option>
                  </select>
                </label>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeCreateForm}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700 transition-colors font-medium text-sm"
                  disabled={creating}
                >
                  {creating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Đang tạo...
                    </span>
                  ) : (
                    'Tạo người dùng'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-1">Quản lý người dùng</h2>
              <p className="text-sm text-gray-600">
                Tổng số:{' '}
                <span className="font-medium text-gray-900">
                  {totalUsers || users.length}
                </span>{' '}
                người dùng
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
              title="Thêm người dùng mới"
            >
              <span className="text-lg text-white">+</span>
              <span className="hidden sm:inline text-white">Thêm người dùng</span>
              <span className="sm:hidden text-white">Thêm</span>
            </button>
          </div>
        </div>

        {/* Fake Users Settings */}
        <div className="p-6 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Cài đặt Fake Users</h3>
              <p className="text-sm text-gray-600">
                Tự động thêm 1-3 users giả mỗi giờ để tăng số lượng users online
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Tổng số fake users hiện tại: <span className="font-semibold">{totalFakeUsers}</span>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${fakeUsersEnabled ? 'text-green-700' : 'text-gray-600'}`}>
                {fakeUsersEnabled ? 'Đã bật' : 'Đã tắt'}
              </span>
              <button
                onClick={handleToggleFakeUsers}
                disabled={loadingFakeUsersSetting}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${fakeUsersEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${fakeUsersEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tìm kiếm</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white placeholder-gray-400"
                placeholder="Tìm theo tên, email, username..."
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
              />
            </div>
            <div className="sm:w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Vai trò</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
              >
                <option value="">Tất cả vai trò</option>
                <option value="student">Học viên</option>
                <option value="instructor">Giảng viên</option>
                <option value="admin">Quản trị</option>
              </select>
            </div>
            <div className="sm:w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Số dòng / trang</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                value={perPage}
                onChange={(e) => {
                  const newPerPage = Number(e.target.value) || 20;
                  setPerPage(newPerPage);
                  // Reset về trang 1 mỗi khi đổi perPage
                  fetchUsers({ page: 1, perPage: newPerPage });
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex gap-2 sm:flex-col sm:justify-end">
              <button
                onClick={() => fetchUsers({ page: 1 })}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={usersLoading}
              >
                {usersLoading ? 'Đang tìm...' : 'Lọc'}
              </button>
              <button
                onClick={handleShowLockedUsers}
                className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={usersLoading}
              >
                <span className="hidden sm:inline text-white">Tài khoản bị khóa</span>
                <span className="sm:hidden text-white">Bị khóa</span>
              </button>
            </div>
          </div>
        </div>

        {/* Balance Summary */}
        <div className="bg-gray-50 border-b border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-white border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Tổng số dư</p>
              <p className="text-xl font-semibold text-gray-900">{formatBalance(totalBalance)}</p>
              <p className="text-xs text-gray-500 mt-1">Từ {userCount} tài khoản</p>
            </div>
            <div className="p-4 rounded-lg bg-white border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Số dư trung bình</p>
              <p className="text-xl font-semibold text-gray-900">{formatBalance(averageBalance)}</p>
              <p className="text-xs text-gray-500 mt-1">Tính trên tất cả người dùng</p>
            </div>
            <div className="p-4 rounded-lg bg-white border border-gray-200">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Người có số dư cao nhất</p>
              {highestBalanceUser ? (
                <>
                  <p className="text-sm font-medium text-gray-900 truncate">{highestBalanceUser.username}</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">
                    {formatBalance(highestBalanceUser.balance)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500 mt-1">Chưa có dữ liệu</p>
              )}
            </div>
          </div>
        </div>

        {/* Table - Desktop */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thông tin</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số dư</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vai trò</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tool TaoThao</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zalo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Xem Key</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thao tác</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <p className="text-sm font-medium">Không có người dùng nào</p>
                      <p className="text-xs text-gray-400">Hãy thử thay đổi bộ lọc hoặc thêm người dùng mới</p>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const isEditing = editingUserId === u.id;
                  const avatarUrl = getMediaUrl(u.avatar);

                  return (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{u.id}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {avatarUrl ? (
                            <img src={avatarUrl} alt={u.username} className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium text-sm flex-shrink-0">
                              {u.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 text-sm truncate">{u.username}</div>
                            {u.full_name && <div className="text-xs text-gray-500 mt-0.5 truncate">{u.full_name}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="text-sm text-gray-900 truncate">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{formatBalance(u.balance)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-red-100 text-red-800' :
                            u.role === 'instructor' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {u.role === 'admin' ? 'Quản trị' : u.role === 'instructor' ? 'Giảng viên' : 'Học viên'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {u.is_locked ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Bị khóa
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Hoạt động
                          </span>
                        )}
                        {(u.failed_login_attempts || 0) > 0 && !u.is_locked && (
                          <div className="text-xs text-orange-700 mt-1.5 bg-orange-50 px-2 py-1 rounded">
                            {(u.failed_login_attempts || 0)}/15 lần sai
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {formatTaothaoUsage(u.taothao_usage)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {u.zalo_name || 'Chưa có'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleKeyAccess(u)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${u.can_view_keys
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          title={u.can_view_keys ? 'Tắt quyền xem key' : 'Kích hoạt quyền xem key'}
                        >
                          {u.can_view_keys ? '✓ Đã kích hoạt' : '✗ Chưa kích hoạt'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {u.is_locked && (
                            <button
                              onClick={() => handleUnlockUser(u)}
                              className="px-2.5 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors"
                              title="Mở khóa tài khoản"
                            >
                              Mở
                            </button>
                          )}
                          {!u.is_locked && (
                            <button
                              onClick={() => handleLockUser(u)}
                              className="px-2.5 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors"
                              title="Khóa tài khoản"
                            >
                              Khóa
                            </button>
                          )}
                          <button
                            onClick={() => startEdit(u)}
                            className="px-2.5 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u)}
                            className="px-2.5 py-1 rounded text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
                          >
                            Xóa
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-gray-200">
          {users.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-block p-4 bg-gray-100 rounded-full mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1">Không có người dùng nào</p>
              <p className="text-xs text-gray-500">Hãy thử thay đổi bộ lọc hoặc thêm người dùng mới</p>
            </div>
          ) : (
            users.map((u) => {
              const isEditing = editingUserId === u.id;
              const avatarUrl = getMediaUrl(u.avatar);

              return (
                <div key={u.id} className="p-4 hover:bg-gray-50 transition-colors">
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={u.username} className="h-12 w-12 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium flex-shrink-0">
                            {u.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            value={editingData.username}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, username: e.target.value }))}
                            placeholder="Username"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <input
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={editingData.full_name}
                          onChange={(e) => setEditingData((prev) => ({ ...prev, full_name: e.target.value }))}
                          placeholder="Họ tên"
                        />
                        <input
                          type="email"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={editingData.email}
                          onChange={(e) => setEditingData((prev) => ({ ...prev, email: e.target.value }))}
                          placeholder="Email"
                        />
                        <select
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={editingData.role}
                          onChange={(e) => setEditingData((prev) => ({ ...prev, role: e.target.value as User['role'] }))}
                        >
                          <option value="student">Học viên</option>
                          <option value="instructor">Giảng viên</option>
                          <option value="admin">Quản trị</option>
                        </select>
                        <input
                          type="password"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                          value={editingData.password}
                          placeholder="Mật khẩu mới (để trống nếu giữ nguyên)"
                          onChange={(e) => setEditingData((prev) => ({ ...prev, password: e.target.value }))}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          className="w-full text-sm border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:border-gray-400 transition-colors"
                          onChange={(e) => setEditingAvatarFile(e.target.files?.[0] || null)}
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => saveEdit(u.id)}
                          className="flex-1 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                          disabled={savingEdit}
                        >
                          {savingEdit ? 'Đang lưu...' : 'Lưu'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex-1 px-4 py-2 rounded-lg bg-gray-200 text-gray-800 text-sm font-medium hover:bg-gray-300 transition-colors"
                        >
                          Hủy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-3 mb-4">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={u.username} className="h-12 w-12 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 font-medium flex-shrink-0">
                            {u.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{u.username}</div>
                          {u.full_name && <div className="text-sm text-gray-600 truncate mt-0.5">{u.full_name}</div>}
                          <div className="text-xs text-gray-500 truncate mt-0.5">{u.email}</div>
                          <div className="text-sm text-gray-900 mt-1.5 font-medium">
                            Số dư: {formatBalance(u.balance)}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-red-100 text-red-800' :
                              u.role === 'instructor' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                            {u.role === 'admin' ? 'Quản trị' : u.role === 'instructor' ? 'Giảng viên' : 'Học viên'}
                          </span>
                          {u.is_locked ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Bị khóa
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Hoạt động
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mb-3">
                        <button
                          onClick={() => handleToggleKeyAccess(u)}
                          className={`w-full px-3 py-1.5 rounded text-xs font-medium transition-colors ${u.can_view_keys
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          title={u.can_view_keys ? 'Tắt quyền xem key' : 'Kích hoạt quyền xem key'}
                        >
                          {u.can_view_keys ? '✓ Key: Đã kích hoạt' : '✗ Key: Chưa kích hoạt'}
                        </button>
                      </div>
                      {(u.phone || u.address || u.taothao_usage || u.zalo_name) && (
                        <div className="text-xs text-gray-600 mb-3 space-y-1 bg-gray-50 rounded-lg p-3">
                          {u.phone && <div><span className="font-medium">SĐT:</span> {u.phone}</div>}
                          {u.address && <div><span className="font-medium">Địa chỉ:</span> {u.address}</div>}
                          {u.taothao_usage && <div><span className="font-medium">Tool TaoThao:</span> {formatTaothaoUsage(u.taothao_usage)}</div>}
                          {u.zalo_name && <div><span className="font-medium">Zalo:</span> {u.zalo_name}</div>}
                        </div>
                      )}
                      {(u.failed_login_attempts || 0) > 0 && !u.is_locked && (
                        <div className="text-xs text-orange-700 mb-3 bg-orange-50 rounded-lg p-2">
                          {(u.failed_login_attempts || 0)}/15 lần đăng nhập sai
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        {u.is_locked && (
                          <button
                            onClick={() => handleUnlockUser(u)}
                            className="flex-1 px-3 py-2 rounded-lg bg-yellow-100 text-yellow-800 text-xs font-medium hover:bg-yellow-200 transition-colors"
                          >
                            Mở khóa
                          </button>
                        )}
                        {!u.is_locked && (
                          <button
                            onClick={() => handleLockUser(u)}
                            className="flex-1 px-3 py-2 rounded-lg bg-yellow-100 text-yellow-800 text-xs font-medium hover:bg-yellow-200 transition-colors"
                          >
                            Khóa
                          </button>
                        )}
                        <button
                          onClick={() => startEdit(u)}
                          className="flex-1 px-3 py-2 rounded-lg bg-blue-100 text-blue-800 text-xs font-medium hover:bg-blue-200 transition-colors"
                        >
                          Sửa
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u)}
                          className="flex-1 px-3 py-2 rounded-lg bg-red-100 text-red-800 text-xs font-medium hover:bg-red-200 transition-colors"
                        >
                          Xóa
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-white flex flex-col sm:flex-row items-center justify-between gap-2 text-xs sm:text-sm">
            <div className="text-gray-600">
              Trang <span className="font-semibold text-gray-900">{page}</span> /{' '}
              <span className="font-semibold text-gray-900">{totalPages}</span>{' '}
              — Hiển thị{' '}
              <span className="font-semibold text-gray-900">
                {startIndex}-{endIndex}
              </span>{' '}
              trên <span className="font-semibold text-gray-900">{currentTotal}</span> người dùng
            </div>
            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleChangePage(page - 1)}
                disabled={page <= 1 || usersLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Trước
              </button>
              <button
                type="button"
                onClick={() => handleChangePage(page + 1)}
                disabled={page >= totalPages || usersLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sau →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminUsersPage;


